import { useState, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import {
  getProjects, updateProject, deleteProject,
  getMilestones, createMilestone, updateMilestone, deleteMilestone,
  reorderProjects, getTeamMembers, getProjectAlertsBulk,
} from '../api/client'
import Modal from '../components/Modal'
import PMSelect from '../components/PMSelect'
import { formatDate } from '../utils/weeks'
import { useAuth } from '../context/AuthContext'
import PageHeader from '../components/PageHeader'
import { useTranslation } from 'react-i18next'
import { alertText } from '../components/alerts/alertText'
import { detalleError } from '../components/admin/detalle'

const COLORS = ['#6366f1','#10b981','#f59e0b','#ef4444','#3b82f6','#8b5cf6','#ec4899','#14b8a6','#f97316','#84cc16']

const EMPTY_PROJECT   = { name: '', code: '', nickname: '', po_pm: '', pm_id: null, description: '', color: '#6366f1', start_date: '', end_date: '', is_active: true, is_purchase_only: false }
const EMPTY_MILESTONE = { name: '', due_date: '', description: '' }

function isSoon(dateStr) {
  if (!dateStr) return false
  const diff = (new Date(dateStr + 'T00:00:00') - new Date().setHours(0,0,0,0)) / 86400000
  return diff >= 0 && diff <= 7
}

function isOverdue(dateStr) {
  if (!dateStr) return false
  return new Date(dateStr + 'T00:00:00') < new Date().setHours(0,0,0,0)
}

export default function Projects() {
  const navigate = useNavigate()
  const { t } = useTranslation('engineering')
  const { user } = useAuth()
  const userRoles = user?.roles || (user?.role ? [user.role] : [])
  const canEdit = userRoles.some(r => r === 'admin' || r === 'pm_po')
  // Creating, deleting, reordering and renaming (name, code) a project are
  // admin's -- Administración > Proyectos; a PM/PO edits the rest.
  const isAdmin = userRoles.includes('admin')

  const [projects, setProjects] = useState([])
  const [members, setMembers] = useState([])
  const [alerts, setAlerts] = useState({})
  const [order, setOrder] = useState([])
  const [milestones, setMilestones] = useState({})
  const [expanded, setExpanded] = useState({})
  const [showInactive, setShowInactive] = useState(false)
  const [loading, setLoading] = useState(true)
  const [modal, setModal] = useState(null)
  const [form, setForm] = useState(EMPTY_PROJECT)
  const [msModal, setMsModal] = useState(null)
  const [msForm, setMsForm] = useState(EMPTY_MILESTONE)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const [dragId, setDragId] = useState(null)
  const [dragOverId, setDragOverId] = useState(null)

  // Whatever the rules flagged for this project, most severe first.
  const alertsFor = (projectId) =>
    alerts[projectId] || alerts[String(projectId)] || []

  const load = async () => {
    setLoading(true)
    const [ps, ms, alertMap] = await Promise.all([
      getProjects(),
      getTeamMembers().catch(() => []),
      // A warning triangle is worth having, but never worth an error page.
      getProjectAlertsBulk().catch(() => ({})),
    ])
    setProjects(ps); setOrder(ps.map(p => p.id))
    setMembers(Array.isArray(ms) ? ms : [])
    setAlerts(alertMap || {})
    setLoading(false)
  }

  const loadMilestones = async (projectId) => {
    const ms = await getMilestones(projectId)
    setMilestones(prev => ({ ...prev, [projectId]: ms }))
  }

  useEffect(() => { load() }, [])

  const onDragStart = (e, id) => { if (!isAdmin) return; setDragId(id); e.dataTransfer.effectAllowed = 'move' }
  const onDragOver  = (e, id) => { if (!isAdmin) return; e.preventDefault(); setDragOverId(id) }
  const onDrop = (e, targetId) => {
    if (!isAdmin) return
    e.preventDefault(); setDragOverId(null)
    if (dragId === targetId) { setDragId(null); return }
    const arr = [...order]
    const fi = arr.indexOf(dragId), ti = arr.indexOf(targetId)
    if (fi === -1 || ti === -1) { setDragId(null); return }
    arr.splice(fi, 1); arr.splice(ti, 0, dragId)
    setOrder(arr); reorderProjects(arr).catch(() => {}); setDragId(null)
  }
  const onDragEnd = () => { setDragId(null); setDragOverId(null) }

  const orderedProjects = order.map(id => projects.find(p => p.id === id)).filter(Boolean)
  const visibleProjects = showInactive ? orderedProjects : orderedProjects.filter(p => p.is_active)

  const toggleExpand = (id) => {
    setExpanded(prev => {
      const next = { ...prev, [id]: !prev[id] }
      if (next[id] && !milestones[id]) loadMilestones(id)
      return next
    })
  }

  const toggleActive = async (p) => { await updateProject(p.id, { is_active: !p.is_active }); load() }

  const openSetup = (id) => navigate(`/admin/proyectos/configurar?id=${id}`)
  const openEdit  = (p) => {
    setForm({ name: p.name, code: p.code || '', nickname: p.nickname || '', po_pm: p.po_pm || '', pm_id: p.pm_id ?? null,
      description: p.description || '', color: p.color, start_date: p.start_date || '',
      end_date: p.end_date || '', is_active: p.is_active, is_purchase_only: p.is_purchase_only || false })
    setError(''); setModal(p)
  }
  const closeModal = () => setModal(null)

  const handleSave = async () => {
    if (!form.name.trim()) { setError(t('projects.nameRequired')); return }
    setSaving(true)
    try {
      const payload = {
        ...form,
        code: form.code.trim() || null, nickname: form.nickname.trim() || null,
        po_pm: (form.po_pm || '').trim() || null,
        pm_id: form.pm_id ?? null,
        start_date: form.start_date || null, end_date: form.end_date || null,
      }
      // Only an admin may send name/code (the backend answers 403 otherwise).
      if (!isAdmin) { delete payload.name; delete payload.code }
      await updateProject(modal.id, payload)
      load(); closeModal()
    } catch (e) { setError(detalleError(e, 'Save failed')) }
    setSaving(false)
  }

  const handleDelete = async (p) => {
    if (!confirm(t('projects.deleteConfirm', {name: p.name}))) return
    try {
      await deleteProject(p.id)
    } catch (e) {
      window.alert(detalleError(e, t('admin:proyectos.deleteFailed')))
    }
    load()
  }

  const openAddMs  = (projectId) => { setMsForm(EMPTY_MILESTONE); setError(''); setMsModal({ projectId }) }
  const openEditMs = (projectId, ms) => {
    setMsForm({ name: ms.name, due_date: ms.due_date || '', description: ms.description || '' })
    setError(''); setMsModal({ projectId, milestone: ms })
  }
  const closeMsModal = () => setMsModal(null)

  const handleSaveMs = async () => {
    if (!msForm.name.trim()) { setError(t('projects.nameRequired')); return }
    setSaving(true)
    try {
      const payload = { ...msForm, due_date: msForm.due_date || null, project_id: msModal.projectId }
      if (!msModal.milestone) await createMilestone(payload)
      else await updateMilestone(msModal.milestone.id, { name: msForm.name, due_date: msForm.due_date || null, description: msForm.description || null })
      loadMilestones(msModal.projectId); closeMsModal()
    } catch (e) { setError(e.response?.data?.detail || 'Save failed') }
    setSaving(false)
  }

  const toggleComplete = async (projectId, ms) => {
    await updateMilestone(ms.id, { completed: !ms.completed }); loadMilestones(projectId)
  }

  const handleDeleteMs = async (projectId, ms) => {
    if (!confirm(t('projects.deleteMilestoneConfirm', {name: ms.name}))) return
    await deleteMilestone(ms.id); loadMilestones(projectId)
  }

  const activeCount   = projects.filter(p => p.is_active).length
  const inactiveCount = projects.length - activeCount

  return (
    <div className="p-8">
      <div className="mb-6">
        <PageHeader
          title={t('projects.title')}
          subtitle={t('projects.activeSuffix', {count: activeCount}) + (inactiveCount > 0 ? t('projects.inactiveSuffix', {count: inactiveCount}) : '')}
          actions={inactiveCount > 0 && (
            <button onClick={() => setShowInactive(s => !s)}
              className={`text-sm px-3 py-2 rounded-lg border transition-colors ${showInactive ? 'bg-gray-100 text-gray-700 border-gray-200' : 'text-gray-500 border-gray-200 hover:bg-gray-50'}`}>
              {showInactive ? t('projects.hideInactive') : t('projects.showInactive', {count: inactiveCount})}
            </button>
          )}
        />
      </div>

      {isAdmin && (
        <div className="mb-4 flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2.5 bg-blue-50 border border-blue-100 rounded-lg text-sm text-blue-800">
          <span>{t('admin:proyectos.banner.gestionadoEn')}</span>
          <Link to="/admin/proyectos" className="font-medium underline hover:text-blue-950">{t('admin:proyectos.banner.irAProyectos')}</Link>
        </div>
      )}

      {loading ? (
        <div className="text-gray-400 text-sm">{t('projects.loading')}</div>
      ) : projects.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <p className="text-sm">{t('projects.noProjects')}</p>
          <p className="text-xs mt-1">{t('projects.noProjectsHint')}</p>
        </div>
      ) : (
        <div className="space-y-2">
          {visibleProjects.map(p => (
            <div key={p.id} draggable={isAdmin}
              onDragStart={e => onDragStart(e, p.id)} onDragOver={e => onDragOver(e, p.id)}
              onDrop={e => onDrop(e, p.id)} onDragEnd={onDragEnd}
              className={`bg-white rounded-xl border overflow-hidden transition-all
                ${p.is_active ? 'border-gray-200' : 'border-gray-100 opacity-60'}
                ${dragId === p.id ? 'opacity-40' : ''}
                ${dragOverId === p.id && dragId !== p.id ? 'border-gray-400 shadow-sm' : ''}
              `}>
              <div className="flex items-center gap-4 px-5 py-3.5">
                {isAdmin && <span className="text-gray-300 cursor-grab text-base select-none">⠿</span>}
                <div className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: p.color }} />

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    {p.code && <span className="text-xs font-mono bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded shrink-0">{p.code}</span>}
                    <button onClick={() => navigate(`/projects/${p.id}`)}
                      className="font-semibold text-gray-900 text-sm hover:text-indigo-600 transition-colors text-left">
                      {p.nickname ? p.nickname : p.name}
                    </button>
                    {p.nickname && <span className="text-xs text-gray-400 truncate max-w-xs" title={p.name}>{p.name}</span>}
                    {alertsFor(p.id).length > 0 && (
                      <button
                        onClick={() => navigate(`/projects/${p.id}`)}
                        title={alertsFor(p.id).map(a => alertText(t, a)).join('\n')}
                        className={`text-sm shrink-0 ${
                          alertsFor(p.id).some(a => a.severity === 'danger')
                            ? 'text-red-600 hover:text-red-700'
                            : 'text-amber-600 hover:text-amber-700'}`}
                      >
                        ⚠{alertsFor(p.id).length > 1 && (
                          <span className="text-[10px] align-super">
                            {alertsFor(p.id).length}
                          </span>
                        )}
                      </button>
                    )}
                    {p.is_purchase_only && (
                      <span className="text-[11px] font-medium bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded shrink-0">
                        {t('projects.purchaseOnly')}
                      </span>
                    )}
                  </div>
                  {(p.start_date || p.end_date || p.po_pm) && (
                    <p className="text-xs text-gray-400 mt-0.5">
                      {p.po_pm && <span className="mr-2">{p.po_pm}</span>}
                      {(p.start_date || p.end_date) && <span>{p.start_date ? formatDate(p.start_date) : '?'} — {p.end_date ? formatDate(p.end_date) : t('projects.ongoing')}</span>}
                    </p>
                  )}
                </div>

                <div className="flex items-center gap-2 shrink-0">
                  {canEdit && (
                    <button onClick={() => toggleActive(p)} title={p.is_active ? t('projects.deactivateTitle') : t('projects.activateTitle')}
                      className={`text-xs px-2.5 py-1 rounded-full font-medium transition-colors cursor-pointer ${
                        p.is_active ? 'bg-emerald-100 text-emerald-700 hover:bg-red-100 hover:text-red-600'
                                    : 'bg-gray-100 text-gray-500 hover:bg-emerald-100 hover:text-emerald-700'
                      }`}>
                      {p.is_active ? t('projects.active') : t('projects.inactive')}
                    </button>
                  )}
                  {!canEdit && (
                    <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${p.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>
                      {p.is_active ? t('projects.active') : t('projects.inactive')}
                    </span>
                  )}

                  <button onClick={() => toggleExpand(p.id)}
                    className="text-xs text-gray-400 hover:text-gray-900 font-medium transition-colors px-2 py-1 rounded hover:bg-gray-100">
                    {expanded[p.id] ? t('projects.hideMilestones') : t('projects.showMilestones')}
                  </button>

                  {canEdit && (
                    <>
                      {isAdmin && <button onClick={() => openSetup(p.id)} className="text-xs text-gray-400 hover:text-gray-900 font-medium transition-colors">{t('projects.setup')}</button>}
                      <button onClick={() => openEdit(p)}    className="text-xs text-gray-400 hover:text-gray-900 font-medium transition-colors">{t('common:actions.edit')}</button>
                      {isAdmin && <button onClick={() => handleDelete(p)} className="text-xs text-gray-400 hover:text-red-500 font-medium transition-colors">{t('common:actions.delete')}</button>}
                    </>
                  )}
                </div>
              </div>

              {expanded[p.id] && (
                <div className="border-t border-gray-100 bg-gray-50 px-5 py-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{t('projects.milestones')}</span>
                    {canEdit && <button onClick={() => openAddMs(p.id)} className="text-xs text-gray-700 hover:underline font-medium">{t('projects.addMilestone')}</button>}
                  </div>
                  {!milestones[p.id] ? (
                    <p className="text-xs text-gray-400">{t('projects.loadingMilestones')}</p>
                  ) : milestones[p.id].length === 0 ? (
                    <p className="text-xs text-gray-400 py-1">{t('projects.noMilestonesYet')}</p>
                  ) : (
                    <ul className="space-y-1.5">
                      {milestones[p.id].map(ms => (
                        <li key={ms.id} className="flex items-center gap-3 group">
                          {canEdit
                            ? <input type="checkbox" checked={ms.completed} onChange={() => toggleComplete(p.id, ms)} className="accent-gray-900" />
                            : <span className={`w-3 h-3 rounded-full shrink-0 ${ms.completed ? 'bg-emerald-400' : 'bg-gray-200'}`} />
                          }
                          <span className={`flex-1 text-sm ${ms.completed ? 'line-through text-gray-400' : 'text-gray-700'}`}>{ms.name}</span>
                          {ms.due_date && (
                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                              ms.completed ? 'bg-gray-100 text-gray-400' :
                              isOverdue(ms.due_date) ? 'bg-red-100 text-red-600' :
                              isSoon(ms.due_date)    ? 'bg-amber-100 text-amber-700' :
                              'bg-gray-100 text-gray-500'
                            }`}>
                              {formatDate(ms.due_date)}{!ms.completed && isOverdue(ms.due_date) && ' ⚠'}
                            </span>
                          )}
                          {canEdit && (
                            <div className="opacity-0 group-hover:opacity-100 transition-opacity flex gap-2">
                              <button onClick={() => openEditMs(p.id, ms)} className="text-xs text-gray-400 hover:text-gray-900">{t('common:actions.edit')}</button>
                              <button onClick={() => handleDeleteMs(p.id, ms)} className="text-xs text-gray-400 hover:text-red-500">×</button>
                            </div>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
          ))}
          {isAdmin && <p className="text-xs text-gray-400 pt-1">{t('projects.dragToReorder')}</p>}
        </div>
      )}

      {/* Project edit modal */}
      {canEdit && modal && (
        <Modal title={t('projects.editProject')} onClose={closeModal}>
          <div className="space-y-4">
            {error && <p className="text-red-500 text-sm bg-red-50 px-3 py-2 rounded-lg">{error}</p>}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('projects.formFullName')}</label>
              <input className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-200 read-only:bg-gray-50 read-only:text-gray-500"
                value={form.name} readOnly={!isAdmin} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('projects.formProjectId')}</label>
              <input className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-gray-200 read-only:bg-gray-50 read-only:text-gray-500"
                value={form.code} readOnly={!isAdmin} onChange={e => setForm(f => ({ ...f, code: e.target.value }))} placeholder="2026-047" />
              {!isAdmin && <p className="text-xs text-gray-400 mt-1">{t('admin:proyectos.soloAdminNombreCodigo')}</p>}
            </div>
            {!form.is_purchase_only && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('projects.formPoPmo')}</label>
                <PMSelect value={form.pm_id} nombre={form.po_pm} members={members}
                  onChange={(id, nombre) => setForm(f => ({ ...f, pm_id: id, po_pm: nombre || '' }))} />
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('projects.formNickname')}</label>
              <input className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-200"
                value={form.nickname} onChange={e => setForm(f => ({ ...f, nickname: e.target.value }))} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('projects.formDescription')}</label>
              <textarea className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-200 resize-none"
                rows={2} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('projects.formColor')}</label>
              <div className="flex gap-2 flex-wrap">
                {COLORS.map(c => (
                  <button key={c} onClick={() => setForm(f => ({ ...f, color: c }))}
                    className={`w-7 h-7 rounded-full transition-transform ${form.color === c ? 'ring-2 ring-offset-2 ring-gray-400 scale-110' : 'hover:scale-110'}`}
                    style={{ backgroundColor: c }} />
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('projects.formStartDate')}</label>
                <input type="date" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-200"
                  value={form.start_date} onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('projects.formEndDate')}</label>
                <input type="date" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-200"
                  value={form.end_date} onChange={e => setForm(f => ({ ...f, end_date: e.target.value }))} />
              </div>
            </div>
            <div className="flex items-center justify-between py-1">
              <label className="text-sm text-gray-700 font-medium">{t('projects.formStatus')}</label>
              <button type="button" onClick={() => setForm(f => ({ ...f, is_active: !f.is_active }))}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${form.is_active ? 'bg-gray-900' : 'bg-gray-300'}`}>
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${form.is_active ? 'translate-x-6' : 'translate-x-1'}`} />
              </button>
            </div>
            <p className="text-xs text-gray-400 -mt-2">{form.is_active ? t('projects.activeDesc') : t('projects.inactiveDesc')}</p>
            <div className="flex items-center justify-between py-1">
              <label className="text-sm text-gray-700 font-medium">{t('projectSetup.purchaseOnly')}</label>
              <button type="button"
                onClick={() => setForm(f => ({ ...f, is_purchase_only: !f.is_purchase_only, po_pm: !f.is_purchase_only ? '' : f.po_pm, pm_id: !f.is_purchase_only ? null : f.pm_id }))}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${form.is_purchase_only ? 'bg-gray-900' : 'bg-gray-300'}`}>
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${form.is_purchase_only ? 'translate-x-6' : 'translate-x-1'}`} />
              </button>
            </div>
            <p className="text-xs text-gray-400 -mt-2">{t('projectSetup.purchaseOnlyHint')}</p>
            <div className="flex gap-3 pt-2">
              <button onClick={handleSave} disabled={saving} className="flex-1 bg-gray-900 text-white py-2 rounded-lg text-sm font-medium hover:bg-gray-700 disabled:opacity-50 transition-colors">{saving ? t('projectSetup.saving') : t('planWeek.save')}</button>
              <button onClick={closeModal} className="flex-1 bg-gray-100 text-gray-700 py-2 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors">{t('planWeek.sendModal.cancel')}</button>
            </div>
          </div>
        </Modal>
      )}

      {/* Milestone modal */}
      {canEdit && msModal && (
        <Modal title={msModal.milestone ? t('projects.editMilestone') : t('projects.addMilestoneModal')} onClose={closeMsModal}>
          <div className="space-y-4">
            {error && <p className="text-red-500 text-sm bg-red-50 px-3 py-2 rounded-lg">{error}</p>}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('projects.nameField')}</label>
              <input className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-200"
                value={msForm.name} onChange={e => setMsForm(f => ({ ...f, name: e.target.value }))} placeholder={t('projects.milestoneNamePlaceholder')} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('projects.dueDateField')}</label>
              <input type="date" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-200"
                value={msForm.due_date} onChange={e => setMsForm(f => ({ ...f, due_date: e.target.value }))} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('projects.descriptionField')}</label>
              <textarea className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-200 resize-none"
                rows={2} value={msForm.description} onChange={e => setMsForm(f => ({ ...f, description: e.target.value }))} />
            </div>
            <div className="flex gap-3 pt-2">
              <button onClick={handleSaveMs} disabled={saving} className="flex-1 bg-gray-900 text-white py-2 rounded-lg text-sm font-medium hover:bg-gray-700 disabled:opacity-50 transition-colors">{saving ? t('projectSetup.saving') : t('planWeek.save')}</button>
              <button onClick={closeMsModal} className="flex-1 bg-gray-100 text-gray-700 py-2 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors">{t('planWeek.sendModal.cancel')}</button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  )
}
